import { DragOverlay } from '@dnd-kit/core';
import { Contract } from 'components/molecules';
import { useFormikContext } from 'formik';
import { createPortal } from 'react-dom';
import {
  AGREEMENT_TYPE,
  IAgreementTemplatetAggregatorForm,
} from 'services/templates/agreement-template/types';
import {
  IContractFormikStatus,
  IContractTemplateFlattenedTreeForm,
  IContractTemplateForm,
  IDistributionType,
  TEMPLATE_TYPE,
} from 'services/templates/types';
import {
  adjustTranslate,
  dropAnimation,
  getChildCount,
  getDistributionSection,
} from 'utils/dnd/dndUtilities';

interface IProjected {
  depth: number;
  maxDepth: number;
  minDepth: number;
  parentId: string | null;
}

interface IContractSortableListItemsProps {
  flattenedItems: IContractTemplateFlattenedTreeForm[];
  activeId: string | null;
  projected: IProjected | null;
  onAddCondition: (id: string, type?: AGREEMENT_TYPE) => void;
  onAddSection: (id: string) => void;
  onAddDigitalDistribution: (id: string) => void;
  onAddPhysicalDistribution: (id: string) => void;
  onAddChannelDistribution: (id: string) => void;
  onAddMerchDistribution: (id: string) => void;
  onAddCustomCondition: (id: string) => void;
  onAddCustomDigitalDistribution: (id: string) => void;
  onAddCustomPhysicalDistribution: (id: string) => void;
  onAddCustomMerchDistribution: (id: string) => void;
  onAddCustomChannelDistribution: (id: string) => void;
  onAddCustomSection: (id: string) => void;
  onAddCustomContract: () => void;
  onDisplayDetail: (
    id: string,
    content: IAgreementTemplatetAggregatorForm
  ) => void;
  onAddShare?: (id: string) => void;
  onRemove: (id: string) => void;
  handleCollapse: (id: string) => void;
}

const ContractSortableListItems = ({
  flattenedItems,
  activeId,
  projected,
  onAddCondition,
  onAddSection,
  onDisplayDetail,
  onAddCustomCondition,
  onAddCustomDigitalDistribution,
  onAddCustomSection,
  onAddMerchDistribution,
  onAddChannelDistribution,
  onAddCustomPhysicalDistribution,
  onAddCustomChannelDistribution,
  onAddCustomMerchDistribution,
  onAddPhysicalDistribution,
  onAddShare,
  onAddCustomContract,
  onAddDigitalDistribution,
  onRemove,
  handleCollapse,
}: IContractSortableListItemsProps) => {
  const { values, status } = useFormikContext<IContractTemplateForm>();
  const { indicator }: IContractFormikStatus = status;

  const digitalDistributionExists = !!getDistributionSection(
    values,
    TEMPLATE_TYPE.digitalDistribution
  );
  const physicalDistributionExists = !!getDistributionSection(
    values,
    TEMPLATE_TYPE.physicalDistribution
  );
  const merchDistributionExists = !!getDistributionSection(
    values,
    TEMPLATE_TYPE.merchDistribution
  );
  const channelDistributionExists = !!getDistributionSection(
    values,
    TEMPLATE_TYPE.channelDistribution
  );

  const getActiveItem = () =>
    activeId ? flattenedItems.find(({ id }) => id === activeId) : null;

  const getActiveItemName = () => {
    const item = getActiveItem();
    let itemName = '-';

    if (item) {
      if (
        item.type === TEMPLATE_TYPE.section ||
        item.type === TEMPLATE_TYPE.contract ||
        item.type === TEMPLATE_TYPE.digitalDistribution ||
        item.type === TEMPLATE_TYPE.merchDistribution ||
        item.type === TEMPLATE_TYPE.channelDistribution ||
        item.type === TEMPLATE_TYPE.physicalDistribution
      ) {
        itemName = item.content;
      }
      if (item.type === TEMPLATE_TYPE.agreement) {
        if (item.content.type === AGREEMENT_TYPE.text) {
          itemName = item.content.textAgreementTemplate?.title ?? '-';
        }
        if (item.content.type === AGREEMENT_TYPE.select) {
          itemName = item.content.selectAgreementTemplate?.title ?? '-';
        }
        if (item.content.type === AGREEMENT_TYPE.defaultShare) {
          itemName = item.content.distributionTemplateShares?.name ?? '-';
        }
        if (item.content.type === AGREEMENT_TYPE.shares) {
          itemName = item.content.distributionTemplateShares?.name ?? '-';
        }
      }
    }

    return itemName;
  };

  const getActiveItemDpeth = () => {
    const item = getActiveItem();
    return item?.depth ?? 0;
  };

  const getParentType = (parentId: string) =>
    flattenedItems.find(({ id }) => id === parentId)?.type as IDistributionType;

  return (
    <>
      {flattenedItems.map(
        (
          {
            collapsed,
            depth,
            type,
            disableDisplayDetail,
            disabledMoreButton,
            disabledCollapse,
            disabledRemove,
            id,
            children,
            content,
            index,
            ...props
          },
          key
        ) => (
          <div className='flex flex-col w-full' key={key}>
            {type === TEMPLATE_TYPE.contract && (
              <Contract.Input.Main
                id={id}
                {...props}
                size='compact'
                depth={0}
                onAddDigitalDistribution={
                  !disabledMoreButton && !digitalDistributionExists
                    ? () => onAddDigitalDistribution(id)
                    : undefined
                }
                onAddCustomDigitalDistribution={
                  !disabledMoreButton && !digitalDistributionExists
                    ? () => onAddCustomDigitalDistribution(id)
                    : undefined
                }
                onAddPhysicalDistribution={
                  !disabledMoreButton && !physicalDistributionExists
                    ? () => onAddPhysicalDistribution(id)
                    : undefined
                }
                onAddMerchDistribution={
                  !disabledMoreButton && !merchDistributionExists
                    ? () => onAddMerchDistribution(id)
                    : undefined
                }
                onAddChannelDistribution={
                  !disabledMoreButton && !channelDistributionExists
                    ? () => onAddChannelDistribution(id)
                    : undefined
                }
                onAddCustomPhysicalDistribution={
                  !disabledMoreButton && !physicalDistributionExists
                    ? () => onAddCustomPhysicalDistribution(id)
                    : undefined
                }
                onAddCustomMerchDistribution={
                  !disabledMoreButton && !merchDistributionExists
                    ? () => onAddCustomMerchDistribution(id)
                    : undefined
                }
                onAddCustomChannelDistribution={
                  !disabledMoreButton && !channelDistributionExists
                    ? () => onAddCustomChannelDistribution(id)
                    : undefined
                }
                onAddSection={
                  !disabledMoreButton ? () => onAddSection(id) : undefined
                }
                onAddCustomSection={
                  !disabledMoreButton ? () => onAddCustomSection(id) : undefined
                }
                onAddCustomContract={
                  !disabledMoreButton ? onAddCustomContract : undefined
                }
              />
            )}
            {type === TEMPLATE_TYPE.digitalDistribution && (
              <Contract.Input.DistributionSection
                id={id}
                {...props}
                size='compact'
                depth={0}
                onAddShare={onAddShare ? () => onAddShare(id) : undefined}
                collapsed={Boolean(collapsed)}
                onCollapse={
                  !disabledCollapse ? () => handleCollapse(id) : undefined
                }
                onRemove={!disabledRemove ? () => onRemove(id) : undefined}
                distributionType={TEMPLATE_TYPE.digitalDistribution}
              />
            )}
            {type === TEMPLATE_TYPE.physicalDistribution && (
              <Contract.Input.DistributionSection
                id={id}
                {...props}
                size='compact'
                depth={0}
                onAddShare={onAddShare ? () => onAddShare(id) : undefined}
                collapsed={Boolean(collapsed)}
                onCollapse={
                  !disabledCollapse ? () => handleCollapse(id) : undefined
                }
                onRemove={!disabledRemove ? () => onRemove(id) : undefined}
                distributionType={TEMPLATE_TYPE.physicalDistribution}
              />
            )}
            {type === TEMPLATE_TYPE.merchDistribution && (
              <Contract.Input.DistributionSection
                id={id}
                {...props}
                size='compact'
                depth={0}
                onAddShare={onAddShare ? () => onAddShare(id) : undefined}
                collapsed={Boolean(collapsed)}
                onCollapse={
                  !disabledCollapse ? () => handleCollapse(id) : undefined
                }
                onRemove={!disabledRemove ? () => onRemove(id) : undefined}
                distributionType={TEMPLATE_TYPE.merchDistribution}
              />
            )}
            {type === TEMPLATE_TYPE.channelDistribution && (
              <Contract.Input.DistributionSection
                id={id}
                {...props}
                size='compact'
                depth={0}
                onAddShare={onAddShare ? () => onAddShare(id) : undefined}
                collapsed={Boolean(collapsed)}
                onCollapse={
                  !disabledCollapse ? () => handleCollapse(id) : undefined
                }
                onRemove={!disabledRemove ? () => onRemove(id) : undefined}
                distributionType={TEMPLATE_TYPE.channelDistribution}
              />
            )}
            {type === TEMPLATE_TYPE.section && (
              <Contract.Input.Section
                id={id}
                {...props}
                size='compact'
                collapsed={Boolean(collapsed)}
                depth={
                  (id === activeId && projected ? projected.depth : depth) - 1
                }
                onCollapse={
                  !disabledCollapse ? () => handleCollapse(id) : undefined
                }
                onAddCondition={
                  !disabledMoreButton ? () => onAddCondition(id) : undefined
                }
                onAddSection={
                  !disabledMoreButton ? () => onAddSection(id) : undefined
                }
                onAddCustomCondition={
                  !disabledMoreButton
                    ? () => onAddCustomCondition(id)
                    : undefined
                }
                onAddCustomSection={
                  !disabledMoreButton ? () => onAddCustomSection(id) : undefined
                }
                onRemove={!disabledRemove ? () => onRemove(id) : undefined}
              />
            )}
            {props.parentId && type === TEMPLATE_TYPE.agreement && (
              <>
                {content.type === AGREEMENT_TYPE.text && (
                  <Contract.Input.Agreement.Text
                    id={id}
                    {...props}
                    depth={
                      (id === activeId && projected ? projected.depth : depth) -
                      1
                    }
                    size='compact'
                    onEditItem={
                      !disableDisplayDetail
                        ? () => onDisplayDetail(id, content)
                        : undefined
                    }
                    onRemove={!disabledRemove ? () => onRemove(id) : undefined}
                  />
                )}
                {content.type === AGREEMENT_TYPE.shares && (
                  <Contract.Input.Agreement.Share
                    id={id}
                    {...props}
                    depth={1}
                    size='compact'
                    onRemove={!disabledRemove ? () => onRemove(id) : undefined}
                    distributionType={getParentType(props.parentId)}
                  />
                )}
                {content.type === AGREEMENT_TYPE.defaultShare && (
                  <Contract.Input.Agreement.DefaultShare
                    id={id}
                    {...props}
                    depth={1}
                    size='compact'
                    onAddShare={
                      onAddShare
                        ? () => onAddShare(props.parentId ?? '')
                        : undefined
                    }
                  />
                )}
                {content.type === AGREEMENT_TYPE.select && (
                  <Contract.Input.Agreement.Select
                    id={id}
                    {...props}
                    depth={
                      (id === activeId && projected ? projected.depth : depth) -
                      1
                    }
                    size='compact'
                    onEditItem={
                      !disableDisplayDetail
                        ? () => onDisplayDetail(id, content)
                        : undefined
                    }
                    onRemove={!disabledRemove ? () => onRemove(id) : undefined}
                  />
                )}
              </>
            )}
          </div>
        )
      )}
      {createPortal(
        <DragOverlay
          dropAnimation={dropAnimation}
          modifiers={indicator ? [adjustTranslate] : undefined}>
          {activeId && getActiveItem() ? (
            <Contract.Input.Wrapper
              id={activeId}
              depth={getActiveItemDpeth() - 1}
              clone
              childCount={getChildCount(values.items, activeId)}>
              {getActiveItemName()}
            </Contract.Input.Wrapper>
          ) : null}
        </DragOverlay>,
        document.body
      )}
    </>
  );
};

export default ContractSortableListItems;
